/*
 *==================================================
 * Licensed Materials - ADIACENT
 *
 * CR022 Autodesk Vault - copy of the SDK StyledTabs
 * (elements/tabs/styled-tabs.tsx) accepting a
 * defaultSelected index: the SDK component hardcodes
 * useState(0) and offers no way to preselect a tab.
 *
 *==================================================
 */
import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

const StyledTabsWrapper = styled.div`
  ${({ theme }) => `
    .MuiAppBar-root {
      box-shadow: none;
    }

    .MuiButtonBase-root {
      font-size: 0.9rem;
      font-weight: 500;
      color: ${theme.palette.text.primary};
      min-width: auto;
      padding: 0 ${theme.spacing(2)}px;
    }

    .MuiTabs-indicator {
      background-color: ${theme.palette.text.primary};
    }

    .MuiTabs-flexContainer {
      background: white;
      color: ${theme.palette.primary.main};
      border-bottom: 2px solid ${theme.palette.grey[300]};
    }
  `}
`;

const StyledTabsPanel = styled.div`
  ${({ theme }) => `
    padding: ${theme.spacing(2)}px;

    ul {
      margin-left: ${theme.spacing(2)}px;
    }
  `}
`;

function TabPanel(props: any) {
  const { children, value, index, name, ...other } = props;

  if (value === index) {
    return (
      <StyledTabsPanel
        role="tabpanel"
        hidden={value !== index}
        id={`${name}-tabpanel-${index}`}
        aria-labelledby={`${name}-tab-${index}`}
        {...other}>
        {children}
      </StyledTabsPanel>
    );
  } else {
    return <></>;
  }
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
  name: PropTypes.string.isRequired,
};

function StyledTabsSelectable({ childrenList, name, defaultSelected }: any) {
  const [value, setValue] = React.useState(0);

  /** the default index is computed asynchronously by the caller (attributes and
   *  attachments load progressively): apply it ONCE when it first arrives, and
   *  never again, so it cannot override the user's own tab clicks */
  const defaultApplied = React.useRef(false);
  React.useEffect(() => {
    if (!defaultApplied.current && defaultSelected > 0 && defaultSelected < childrenList.length) {
      defaultApplied.current = true;
      setValue(defaultSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelected]);

  const handleChange = (_: any, newValue: any) => {
    setValue(newValue);
  };

  function a11yProps(index: number) {
    return {
      id: `${name}-tab-${index}`,
      "aria-controls": `${name}-tabpanel-${index}`,
    };
  }

  return (
    <StyledTabsWrapper>
      <AppBar position="static">
        <Tabs value={value} onChange={handleChange} aria-label="tabs example">
          {childrenList.map((v: any, index: number) => (
            <Tab key={index} label={v.title} {...a11yProps(index)} />
          ))}
        </Tabs>
      </AppBar>
      {childrenList.map((v: any, index: number) => (
        <TabPanel key={index} value={value} index={index} name={name}>
          {v.tabContent}
        </TabPanel>
      ))}
    </StyledTabsWrapper>
  );
}

StyledTabsSelectable.propTypes = {
  childrenList: PropTypes.any.isRequired,
  name: PropTypes.string.isRequired,
  defaultSelected: PropTypes.number,
};

export { StyledTabsSelectable };
